import { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { X, ExternalLink, Loader2, AlertTriangle, Copy, Check, Printer, FileDown } from 'lucide-react';
import { exportNodeAsPdf } from '@/lib/pdfExport';
import PdfModeModal, { getSavedPdfMode } from '@/components/PdfModeModal';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * FilingViewer — May 2026.
 *
 * Full-tab SEC filing reader. Fetches `/api/sec/filing-html?url=...`
 * which downloads the .txt submission from EDGAR, parses out the
 * primary HTML document, and returns bleach-sanitised HTML safe to
 * render. The user sees the actual 10-K / 8-K with tables, headings,
 * and formatting intact — no bounce to sec.gov.
 *
 * The component fills the parent tab's content area entirely; the X
 * button in the top-right calls `onClose` to restore the previous tab
 * state. The original SEC URL is always offered as a fallback link in
 * case the in-app render misses something (XBRL footnotes etc.).
 */
export default function FilingViewer({ url, label, onClose, user = null, compact = false }) {
  const [html, setHtml] = useState(null);
  const [title, setTitle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [errored, setErrored] = useState(false);
  const [copied, setCopied] = useState(false);
  const [pdfBusy, setPdfBusy] = useState(false);
  const [pdfModalOpen, setPdfModalOpen] = useState(false);
  // Ref on the body div so the copy button can read its `innerText`
  // (renders the sanitised, image-stripped HTML as readable text in
  // the same order the user sees).
  const bodyRef = useRef(null);

  // Stable file/window names — `label` is "AAPL 10-K · Business" etc.
  // so a slugified copy makes a clean PDF filename.
  const slug = (label || title || 'sec-filing')
    .toString()
    .toLowerCase()
    .replace(/[^\w]+/g, '-')
    .replace(/^-+|-+$/g, '');

  const handlePrint = () => {
    if (!html) return;
    // Open the filing in a clean popup window with its own
    // <style> block so the user prints ONLY the SEC document —
    // never the app chrome / modal / tabs.
    //
    // We MUST NOT pass `noopener` here: Chrome returns `null`
    // from `window.open(..., '..., noopener,...')` even on
    // success, which would trigger the wrong fallback and end up
    // printing the entire app page (which was the user's bug
    // report). Without `noopener`, the new window is owned by us
    // and we can call .document.write + .print on it safely.
    const win = window.open('', '_blank', 'width=900,height=1100');
    if (!win) {
      // Genuine pop-up block. Tell the user instead of silently
      // printing the modal-laden main window like before.
      alert('Print blocked: please allow pop-ups for this site so the filing can open in its own window for printing.');
      return;
    }
    win.document.open();
    win.document.write(`<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="utf-8" />
<title>${(label || title || 'SEC Filing').replace(/[<>]/g, '')}</title>
<style>
  body { font-family: Georgia, 'Times New Roman', serif; color: #111; margin: 24px; line-height: 1.55; }
  h1, h2, h3, h4 { font-family: Helvetica, Arial, sans-serif; }
  table { border-collapse: collapse; margin: 12px 0; width: 100%; }
  table td, table th { border: 1px solid #ccc; padding: 4px 6px; vertical-align: top; }
  .filing-print-meta { font-size: 11px; color: #666; border-bottom: 1px solid #ddd; padding-bottom: 8px; margin-bottom: 16px; }
  .filing-print-meta strong { color: #111; }
  @page { margin: 18mm 14mm 18mm 14mm; }
</style>
</head>
<body>
<div class="filing-print-meta">
  <strong>${(label || 'SEC Filing').replace(/[<>]/g, '')}</strong>
  &nbsp;·&nbsp; Source: <a href="${url.replace(/"/g, '')}">${url.replace(/[<>]/g, '')}</a>
  &nbsp;·&nbsp; Printed via twylightcrow.com on ${new Date().toLocaleDateString()}
</div>
${html}
</body>
</html>`);
    win.document.close();
    // Give the new doc a tick to layout before invoking print.
    setTimeout(() => {
      try {
        win.focus();
        win.print();
      } catch { /* user-cancelled */ }
    }, 400);
  };

  // PDF flow now mirrors the Tax Account Audit / Year-End Report
  // pages: pop the shared `PdfModeModal` so the user picks
  // Download / Open in new tab / Both (and we remember the choice
  // via getSavedPdfMode). If they previously saved a preference,
  // we skip the modal and run that mode directly.
  const handlePdf = async (mode = 'download') => {
    if (!html) return;
    setPdfBusy(true);
    try {
      await exportNodeAsPdf({
        elementId: 'sec-filing-pdf-root',
        filename: `${slug || 'sec-filing'}.pdf`,
        mode,
        user,
      });
    } finally {
      setPdfBusy(false);
    }
  };
  const onPdfClick = () => {
    const saved = getSavedPdfMode();
    if (saved) {
      handlePdf(saved);
    } else {
      setPdfModalOpen(true);
    }
  };

  useEffect(() => {
    if (!url) return;
    let cancelled = false;
    setLoading(true);
    setErrored(false);
    setHtml(null);
    axios.get(`${API}/sec/filing-html`, {
      params: { url },
      withCredentials: true,
    })
      .then((r) => {
        if (cancelled) return;
        setHtml(r.data?.html || '');
        setTitle(r.data?.title || null);
      })
      .catch(() => { if (!cancelled) setErrored(true); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [url]);

  return (
    <div
      className={
        compact
          ? "flex flex-col rounded-lg border bg-card overflow-hidden min-h-[40vh] max-h-[65vh]"
          : "flex flex-col h-full min-h-[60vh]"
      }
      data-testid="sec-filing-viewer"
    >
      {/* Sticky header bar with close + external fallback */}
      <div className="flex items-center justify-between gap-2 px-3 py-2 border-b bg-card sticky top-0 z-10">
        <div className="flex items-center gap-2 min-w-0">
          <span className="text-[11px] uppercase tracking-wide font-bold text-muted-foreground">SEC Filing</span>
          {label && (
            <span className="text-sm font-bold truncate">{label}</span>
          )}
          {title && title !== label && (
            <span className="text-xs text-muted-foreground truncate">· {title}</span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={async () => {
              // Pull readable text from the rendered DOM rather than
              // the raw HTML so headings, tables, and lists collapse
              // into the same visual order the user is looking at.
              // Fallback to navigator.clipboard with the bleach-cleaned
              // HTML's text version if the ref isn't ready yet.
              const text = bodyRef.current?.innerText || (html || '').replace(/<[^>]+>/g, ' ');
              try {
                await navigator.clipboard.writeText(text);
                setCopied(true);
                setTimeout(() => setCopied(false), 1500);
              } catch {
                // Old browsers / permission denied — fall back to
                // legacy execCommand path.
                try {
                  const ta = document.createElement('textarea');
                  ta.value = text;
                  ta.style.position = 'fixed';
                  ta.style.opacity = '0';
                  document.body.appendChild(ta);
                  ta.select();
                  document.execCommand('copy');
                  document.body.removeChild(ta);
                  setCopied(true);
                  setTimeout(() => setCopied(false), 1500);
                } catch { /* give up silently */ }
              }
            }}
            disabled={loading || errored || !html}
            className="inline-flex items-center gap-1 text-[11px] font-semibold text-muted-foreground hover:text-foreground disabled:opacity-40 disabled:cursor-not-allowed"
            data-testid="sec-filing-viewer-copy"
            title="Copy the full filing text to clipboard"
          >
            {copied ? (
              <>
                <Check className="w-3 h-3 text-emerald-500" />
                Copied
              </>
            ) : (
              <>
                <Copy className="w-3 h-3" />
                Copy
              </>
            )}
          </button>
          <button
            type="button"
            onClick={handlePrint}
            disabled={loading || errored || !html}
            className="inline-flex items-center gap-1 text-[11px] font-semibold text-muted-foreground hover:text-foreground disabled:opacity-40 disabled:cursor-not-allowed"
            data-testid="sec-filing-viewer-print"
            title="Print the filing (opens a clean print window)"
          >
            <Printer className="w-3 h-3" />
            Print
          </button>
          <button
            type="button"
            onClick={onPdfClick}
            disabled={loading || errored || !html || pdfBusy}
            className="inline-flex items-center gap-1 text-[11px] font-semibold text-muted-foreground hover:text-foreground disabled:opacity-40 disabled:cursor-not-allowed"
            data-testid="sec-filing-viewer-pdf"
            title="Save the filing as a PDF (download or open in new tab)"
          >
            <FileDown className="w-3 h-3" />
            {pdfBusy ? 'Saving…' : 'PDF'}
          </button>
          <a
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-[11px] text-muted-foreground hover:text-foreground"
            title="Open the raw .txt submission on SEC.gov"
          >
            Source <ExternalLink className="w-3 h-3" />
          </a>
          <button
            type="button"
            onClick={onClose}
            className={
              compact
                ? "inline-flex items-center justify-center w-7 h-7 rounded-md text-rose-600 dark:text-rose-400 hover:text-white hover:bg-rose-600 dark:hover:bg-rose-700 border border-rose-300 dark:border-rose-800 transition-colors"
                : "inline-flex items-center justify-center w-7 h-7 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted/40 transition-colors"
            }
            data-testid="sec-filing-viewer-close"
            aria-label="Close filing viewer"
            title="Close — return to the tab"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Body */}
      <div className="flex-1 overflow-y-auto">
        {loading && (
          <div className="flex items-center justify-center gap-2 py-12 text-sm text-muted-foreground">
            <Loader2 className="w-4 h-4 animate-spin" />
            Loading filing from SEC EDGAR…
          </div>
        )}
        {errored && (
          <div className="px-4 py-8 text-center">
            <AlertTriangle className="w-6 h-6 mx-auto text-amber-500 mb-2" />
            <p className="text-sm text-foreground mb-1">Couldn&apos;t render this filing in-app.</p>
            <p className="text-xs text-muted-foreground mb-3">
              SEC EDGAR may be rate-limiting or the filing format is unusual.
            </p>
            <a
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-xs font-semibold text-primary hover:underline"
            >
              Open on SEC.gov <ExternalLink className="w-3 h-3" />
            </a>
          </div>
        )}
        {!loading && !errored && html && (
          // Filing HTML is bleach-sanitised server-side. We trust the
          // backend to have stripped <script>, event handlers, and any
          // disallowed protocol on attributes. `sec-filing-html` class
          // applies the Learn-style typography defaults below so SEC
          // documents render with readable type sizes and table
          // borders, regardless of whatever inline styles the issuer
          // baked into their original filing.
          //
          // `id="sec-filing-pdf-root"` + `data-pdf-root` make the
          // body addressable by `exportNodeAsPdf` for the PDF button
          // in the header. The shared helper handles page-break
          // avoidance, footer stamping, and font-baseline guards.
          <div
            id="sec-filing-pdf-root"
            data-pdf-root
            className="px-4 py-4"
          >
            <div
              ref={bodyRef}
              className="sec-filing-html prose prose-sm max-w-none dark:prose-invert"
              dangerouslySetInnerHTML={{ __html: html }}
              data-testid="sec-filing-viewer-body"
            />
          </div>
        )}
      </div>

      {/* PDF Mode picker — same modal the Tax Account Audit and
          Year-End Report use. Mounted in the viewer's flow so it
          sits above the modal backdrop without portal headaches. */}
      <PdfModeModal
        open={pdfModalOpen}
        onClose={() => setPdfModalOpen(false)}
        onApply={(mode) => handlePdf(mode)}
        title="Save / View SEC Filing PDF"
      />
    </div>
  );
}
